import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {parseMethodNodeFromNetwork} from 'procedures/network/ProcedureNetworkTranslators';
import {TaskDefinitionVersionSpec, TaskSchemaSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {getSchemaFieldInfoFromHandle} from './flowchartIdentifierUtils';
import {
  InteractiveTemplateFlowchart,
  ProcessFlowchartEdgeType,
  TaskDefinitionVersionSpecEdge,
  TaskDefinitionVersionSpecFlowchart,
} from './processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from './processFlowchartConfigEditorConstants';
import {TaskDefinitionVersionSpecWithTaskSchema} from './processFlowchartConfigEditorNodeTypes';

export function convertApplicationDataToSpec(
  applicationData: ProcessTaskDefinitionVersionGraphQL['applicationData']
): unknown {
  if (applicationData == null) {
    return null;
  }
  if (isInaccessible(applicationData)) {
    return null;
  }

  if (applicationData.__typename === 'ProcedureMethodDefinitionVersionNode') {
    return parseMethodNodeFromNetwork(applicationData, (id, errorContext) => id);
  }

  assert(false, 'Unknown application data type', {applicationData: assertNever(applicationData)});
}

export function convertTaskDefinitionVersionToSpec(
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL,
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL>
): TaskDefinitionVersionSpecWithTaskSchema {
  let taskSchema: TaskSchemaSpec | null = null;

  const taskDefinitionSchema = taskDefinitionVersion.processTaskSchema;
  assert(!isInaccessible(taskDefinitionSchema), 'Task definition schema is unexpectedly inaccessible', {
    taskDefinitionVersion,
    taskDefinitionSchema,
  });
  const serverTaskSchema = allTaskSchemas.find((schema) => schema.id === taskDefinitionSchema.id);
  assert(serverTaskSchema != null, `Server task schema not found ${taskDefinitionSchema.id}`, {
    taskDefinitionSchema,
  });

  taskSchema = {
    id: taskDefinitionSchema.id,
    name: taskDefinitionSchema.name,
    executionType: taskDefinitionSchema.executionType,
    fields: serverTaskSchema.fields,
    outputSchema:
      serverTaskSchema.outputSchema != null
        ? {
            id: serverTaskSchema.outputSchema.id,
            name: serverTaskSchema.outputSchema.name,
            fields: serverTaskSchema.outputSchema.fields,
          }
        : null,
  };

  const applicationDataSpec = convertApplicationDataToSpec(taskDefinitionVersion.applicationData);
  return {
    clientId: taskDefinitionVersion.id,
    currentVersionId: taskDefinitionVersion.id,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskSchema.id,
      applicationData: applicationDataSpec,
      taskSchema,
    },
  };
}

/**
 * Converts a frontend TaskDefinitionVersionSpecWithTaskSchema into a network spec TaskDefinitionVersionSpec.
 * This functionally removes the task schema from the spec, as it is not needed for updates.
 */
export function convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema
): TaskDefinitionVersionSpec {
  return {
    clientId: taskDefinitionVersionSpec.clientId,
    currentVersionId: taskDefinitionVersionSpec.currentVersionId,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.id,
      applicationData: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData,
    },
  };
}

/**
 * Converts the ReactFlow nodes and edges into a network spec TaskDefinitionVersionSpecFlowchart,
 * so that application teams can just work in terms of task definition versions.
 */
export function convertInteractiveFlowchartToSpecFlowchart(
  flowchart: InteractiveTemplateFlowchart
): TaskDefinitionVersionSpecFlowchart {
  const nodes: Record<string, TaskDefinitionVersionSpec> = {};
  for (const node of flowchart.nodes) {
    nodes[node.id] = convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
      node.data.taskDefinitionVersionSpec
    );
  }

  const edges: Array<TaskDefinitionVersionSpecEdge> = [];
  for (const edge of flowchart.edges) {
    const edgeSpec: TaskDefinitionVersionSpecEdge = {
      fromNodeClientId: edge.source,
      toNodeClientId: edge.target,
      type: edge.type,
    };

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      edgeSpec.fromFieldDefinitionId = getSchemaFieldInfoFromHandle(edge.sourceHandle).fieldId;
      edgeSpec.toFieldDefinitionId = getSchemaFieldInfoFromHandle(edge.targetHandle).fieldId;
    }

    edges.push(edgeSpec);
  }

  return {
    nodes,
    edges,
  };
}
